class PrimerryPdpIngredients extends HTMLElement {
  connectedCallback() {
    window.clearTimeout(this.destroyTimer);
    this.watchContentHeights();
    this.swiperElement = this.querySelector('[data-ingredient-swiper]');
    if (!this.swiperElement || this.swiper || this.initializing) return;
    this.initializing = true;
    this.initSwiper();
  }

  // Every text box on the rail takes the height of the tallest one, so the headings line
  // up and a short description leaves space at the bottom. Unnati, 5 Oct 2026: "All
  // headings should be aligned and block heights should be same". A fixed two-line
  // reserve in CSS fell short when Fennel's copy ran to three lines; the CSS min-height
  // stays as the no-script floor.
  watchContentHeights() {
    if (this.contentObserver) return;
    const equalize = () => {
      const boxes = Array.from(this.querySelectorAll('.primerry-pdp-ingredients__content'));
      if (!boxes.length) return;
      boxes.forEach((box) => { box.style.minHeight = ''; });
      const tallest = Math.max(...boxes.map((box) => box.offsetHeight));
      boxes.forEach((box) => { box.style.minHeight = `${tallest}px`; });
    };
    this.contentObserver = new ResizeObserver(() => window.requestAnimationFrame(equalize));
    this.contentObserver.observe(this);
    document.fonts?.ready.then(equalize);
    equalize();
  }

  disconnectedCallback() {
    this.contentObserver?.disconnect();
    this.contentObserver = null;
    this.destroyTimer = window.setTimeout(() => {
      if (this.isConnected) return;
      this.swiper?.destroy(true, true);
      this.swiper = null;
      this.initializing = false;
    });
  }

  async initSwiper() {
    if (!this.swiperElement) return;

    try {
      const SwiperClass = await this.ensureSwiperAssets();
      if (!this.isConnected) return;

      const slideCount = Number(this.swiperElement.dataset.slideCount) || 0;
      const requestedSlide = Number(this.dataset.initialSlide) || 0;
      const initialSlide = Math.min(requestedSlide, Math.max(slideCount - 1, 0));
      // The PDP ingredient rail, [data-ingredient-swiper] inside .primerry-pdp-ingredients__swiper:
      // no cloned slides and no wrap-around. Kanak, 16 Sep 2026, "the cards currently have
      // endless scrolling. Remove the endless scroll." The rail still scrolls by hand, by
      // touch on a phone and by a two-finger trackpad push on a desktop, which is what
      // freeMode and mousewheel below do; only the illusion of a rail with no end is gone.
      const renderedSlideCount = slideCount;
      const renderedInitialSlide = initialSlide;

      this.swiper = new SwiperClass(this.swiperElement, {
        slidesPerView: 'auto',
        centeredSlides: false,
        centeredSlidesBounds: false,
        centerInsufficientSlides: false,
        initialSlide: renderedInitialSlide,
        loop: false,
        spaceBetween: 8,
        speed: 650,
        grabCursor: true,
        simulateTouch: true,
        allowTouchMove: true,
        threshold: 4,
        touchRatio: 1,
        resistance: true,
        resistanceRatio: 0.72,
        longSwipesRatio: 0.15,
        longSwipesMs: 250,
        watchOverflow: true,
        freeMode: {
          enabled: true,
          momentum: true,
          momentumBounce: false,
          momentumRatio: 0.82,
          momentumVelocityRatio: 0.8,
          minimumVelocity: 0.02,
        },
        mousewheel: {
          enabled: true,
          forceToAxis: true,
          releaseOnEdges: true,
          sensitivity: 0.65,
          thresholdDelta: 2,
        },
        breakpoints: {
          750: { spaceBetween: 12 },
        },
        observer: true,
        observeParents: true,
        resizeObserver: true,
        watchSlidesProgress: true,
        updateOnWindowResize: true,
      });

      requestAnimationFrame(() => {
        if (!this.swiper || this.swiper.destroyed) return;
        this.swiper.update();
        if (this.swiper.params.loop) {
          this.swiper.slideToLoop(renderedInitialSlide, 0, false);
        } else {
          this.swiper.slideTo(renderedInitialSlide, 0, false);
        }
      });
    } catch (error) {
      this.dataset.swiperFallback = 'true';
    } finally {
      this.initializing = false;
    }
  }

  createLoopCopies(slideCount) {
    const wrapper = this.swiperElement.querySelector('.swiper-wrapper');
    if (!wrapper || slideCount < 1) return 0;

    if (wrapper.dataset.loopCopies === 'true') {
      return wrapper.children.length;
    }

    const originalSlides = Array.from(wrapper.children).slice(0, slideCount);
    const before = document.createDocumentFragment();
    const after = document.createDocumentFragment();

    originalSlides.forEach((slide) => {
      const clone = slide.cloneNode(true);
      clone.dataset.ingredientClone = 'true';
      before.appendChild(clone);
    });

    originalSlides.forEach((slide) => {
      const clone = slide.cloneNode(true);
      clone.dataset.ingredientClone = 'true';
      after.appendChild(clone);
    });

    wrapper.prepend(before);
    wrapper.append(after);
    wrapper.dataset.loopCopies = 'true';
    return wrapper.children.length;
  }

  async ensureSwiperAssets() {
    const stylesheetPromise = new Promise((resolve, reject) => {
      let stylesheet = document.querySelector('link[href*="swiper-bundle.min.css"]');

      if (stylesheet?.sheet) {
        resolve();
        return;
      }

      if (!stylesheet) {
        stylesheet = document.createElement('link');
        stylesheet.rel = 'stylesheet';
        stylesheet.href = 'https://cdn.jsdelivr.net/npm/swiper@14.2.0/swiper-bundle.min.css';
        stylesheet.dataset.primerrySwiperStyle = 'true';
        document.head.appendChild(stylesheet);
      }

      stylesheet.addEventListener('load', resolve, { once: true });
      stylesheet.addEventListener('error', () => reject(new Error('Swiper stylesheet failed to load.')), { once: true });
    });

    const scriptPromise = new Promise((resolve, reject) => {
      if (window.Swiper) {
        resolve(window.Swiper);
        return;
      }

      let script = document.querySelector('script[src*="swiper-bundle.min.js"]');

      if (!script) {
        script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/swiper@14.2.0/swiper-bundle.min.js';
        script.defer = true;
        script.dataset.primerrySwiperScript = 'true';
        document.head.appendChild(script);
      }

      script.addEventListener('load', () => resolve(window.Swiper), { once: true });
      script.addEventListener('error', () => reject(new Error('Swiper script failed to load.')), { once: true });
    });

    const [, SwiperClass] = await Promise.all([stylesheetPromise, scriptPromise]);
    if (!SwiperClass) throw new Error('Swiper failed to initialize.');
    return SwiperClass;
  }
}

if (!customElements.get('primerry-pdp-ingredients')) {
  customElements.define('primerry-pdp-ingredients', PrimerryPdpIngredients);
}
